import React, { useState, useEffect } from "react";
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  Select,
  Text,
} from "@chakra-ui/react";
import SafeAreaContainer from "../../../components/SafeAreaContainer";
import { updateManagerPassword, updateSecurityPassword, getSecurities } from "./service";
import { useSelector } from "react-redux";
import { FormInputs, CustomInputGroup } from "../../../components/FormInput";
import { PrimaryBTN } from "../../../components/CustomButtons";
import { _COLORS } from "../../../app/constants";
import { errorNotifier, successNotifier } from "../../../components/NotificationHandler";

const PasswordManagement = () => {
  const userId = useSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(false);

  // Manager password form state
  const [managerPasswords, setManagerPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Security states
  const [securities, setSecurities] = useState([]);
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [securityPassword, setSecurityPassword] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Fetch securities on component mount
  useEffect(() => {
    const fetchSecurities = async () => {
      try {
        const data = await getSecurities(userId);
        setSecurities(data);
      } catch (error) {
        // Error already handled in service
      }
    };
    fetchSecurities();
  }, [userId]);

  const handleManagerPasswordSubmit = async (e) => {
    e.preventDefault();
    if (managerPasswords.newPassword !== managerPasswords.confirmPassword) {
      errorNotifier("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await updateManagerPassword(userId, {
        currentPassword: managerPasswords.oldPassword,
        newPassword: managerPasswords.newPassword,
      });
      setManagerPasswords({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      // Error is already handled in the service
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecurityPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSecurity) {
      errorNotifier("Please select a security");
      return;
    }
    
    if (securityPassword.newPassword !== securityPassword.confirmPassword) {
      errorNotifier("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await updateSecurityPassword(selectedSecurity._id, {
        newPassword: securityPassword.newPassword,
      });
      setSecurityPassword({
        newPassword: "",
        confirmPassword: "",
      });
      setSelectedSecurity(null);
    } catch (error) {
      // Error is already handled in the service
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaContainer>
      <Box bg="white" borderRadius="10px" p={6} margin="20px">
        <Tabs isFitted variant="enclosed">
          <TabList mb="1em">
            <Tab 
              _selected={{ 
                color: _COLORS.brand,
                borderColor: _COLORS.brand,
                borderBottom: "2px solid"
              }}
            >
              Manager Password
            </Tab>
            <Tab
              _selected={{ 
                color: _COLORS.brand,
                borderColor: _COLORS.brand,
                borderBottom: "2px solid"
              }}
            >
              Security Password
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <form onSubmit={handleManagerPasswordSubmit}>
                <VStack spacing={4} align="stretch">
                  <CustomInputGroup
                    label="Current Password"
                    type="password"
                    value={managerPasswords.oldPassword}
                    handleChange={(e) =>
                      setManagerPasswords({
                        ...managerPasswords,
                        oldPassword: e.target.value,
                      })
                    }
                  />
                  <CustomInputGroup
                    label="New Password"
                    type="password"
                    value={managerPasswords.newPassword}
                    handleChange={(e) =>
                      setManagerPasswords({
                        ...managerPasswords,
                        newPassword: e.target.value,
                      })
                    }
                  />
                  <CustomInputGroup
                    label="Confirm New Password"
                    type="password"
                    value={managerPasswords.confirmPassword}
                    handleChange={(e) =>
                      setManagerPasswords({
                        ...managerPasswords,
                        confirmPassword: e.target.value,
                      })
                    }
                  />
                  <PrimaryBTN
                    type="submit"
                    bg={_COLORS.brand}
                    isLoading={isLoading}
                    loadingText="Updating..."
                  >
                    Update Password
                  </PrimaryBTN>
                </VStack>
              </form>
            </TabPanel>
            <TabPanel>
              <form onSubmit={handleSecurityPasswordSubmit}>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text mb={2} color="#515151" fontSize="18px">Select Security</Text>
                    <Select
                      placeholder="Select a security"
                      value={selectedSecurity?._id || ""}
                      onChange={(e) => {
                        const security = securities.find(s => s._id === e.target.value);
                        setSelectedSecurity(security);
                      }}
                      h="48px"
                      isRequired
                    >
                      {securities.map((security) => (
                        <option key={security._id} value={security._id}>
                          {security.email} - {security.phoneNumber}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  
                  {selectedSecurity && (
                    <>
                      <CustomInputGroup
                        label="New Security Password"
                        type="password"
                        value={securityPassword.newPassword}
                        handleChange={(e) =>
                          setSecurityPassword({
                            ...securityPassword,
                            newPassword: e.target.value,
                          })
                        }
                      />
                      <CustomInputGroup
                        label="Confirm Security Password"
                        type="password"
                        value={securityPassword.confirmPassword}
                        handleChange={(e) =>
                          setSecurityPassword({
                            ...securityPassword,
                            confirmPassword: e.target.value,
                          })
                        }
                      />
                    </>
                  )}
                  
                  <PrimaryBTN
                    type="submit"
                    bg={_COLORS.brand}
                    isLoading={isLoading}
                    loadingText="Updating..."
                    isDisabled={!selectedSecurity}
                  >
                    Update Security Password
                  </PrimaryBTN>
                </VStack>
              </form>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </SafeAreaContainer>
  );
};

export default PasswordManagement;
